import React, {
  createContext,
  ReactNode,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef
} from 'react'
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  browserLocalPersistence,
  User
} from 'firebase/auth'
import { isTokenExpired, notifyError, notifySuccess } from 'utils'
import { auth } from '../config/firebase'

interface AuthContextType {
  user: User | null;
  token: string | null;
  role: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  selectedClientId: string | null;
  setSelectedClientId: (id: string | null) => void;
  refreshToken: () => void;
  loading: boolean;
  loadingRole: boolean;
}

const allowedDomains = [
  '@excelerisconsulting.com',
  '@excelerisconsulting.com.au'
]

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null)
  const [selectedClientId, setSelectedClientId] = useState<string | null>(
    localStorage.getItem('selectedClientId')
  )
  const [loading, setLoading] = useState<boolean>(true)
  const [role, setRole] = useState<string | null>(localStorage.getItem('role'))
  const [token, setToken] = useState<string | null>(
    localStorage.getItem('token')
  )
  const [loadingRole, setLoadingRole] = useState<boolean>(true)
  const [lastActivityTime, setLastActivityTime] = useState<number>(Date.now())

  const logoutCalledRef = useRef(false)
  const isLoggingIn = useRef(false)
  const isAutoLogoutCalledRef = useRef(false)

  const logout = useCallback(async () => {
    if (logoutCalledRef.current) return
    logoutCalledRef.current = true

    try {
      await signOut(auth)
      setUser(null)
      setRole(null)
      setToken(null)
      localStorage.clear()
      notifySuccess('Logout successful')
    } catch (error) {
      notifyError('Logout failed')
    } finally {
      logoutCalledRef.current = false
    }
  }, [])

  const refreshToken = useCallback(async () => {
    if (!auth.currentUser) return
    try {
      const refreshedToken = await auth.currentUser.getIdToken(true)
      if (isTokenExpired(refreshedToken)) {
        if (!isAutoLogoutCalledRef.current) {
          isAutoLogoutCalledRef.current = true
          notifyError('Session expired, please log in again')
          logout()
        }
      } else {
        localStorage.setItem('token', refreshedToken)
        setToken(refreshedToken)
      }
    } catch (error) {
      notifyError('Error refreshing token')
      if (!isAutoLogoutCalledRef.current) {
        isAutoLogoutCalledRef.current = true
        logout()
      }
    }
  }, [logout])

  useEffect(() => {
    const checkInactivity = () => {
      const currentTime = Date.now()
      const timeSinceLastAction = currentTime - lastActivityTime

      if (timeSinceLastAction >= 60 * 60 * 1000) {
        if (!isAutoLogoutCalledRef.current) {
          isAutoLogoutCalledRef.current = true
          notifyError('Session expired due to inactivity.')
          logout()
        }
      } else if (
        timeSinceLastAction >= 30 * 60 * 1000 &&
        timeSinceLastAction < 59 * 60 * 1000
      ) {
        refreshToken()
      }
    }

    const interval = setInterval(checkInactivity, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [lastActivityTime, logout, refreshToken])

  useEffect(() => {
    const resetTimer = () => setLastActivityTime(Date.now())

    ;['mousemove', 'keydown', 'click', 'scroll'].forEach((event) =>
      window.addEventListener(event, resetTimer)
    )

    return () => {
      ;['mousemove', 'keydown', 'click', 'scroll'].forEach((event) =>
        window.removeEventListener(event, resetTimer)
      )
    }
  }, [])
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        if (currentUser?.email) {
          const email = currentUser?.email
          const isAllowedDomain = allowedDomains.some((domain) =>
            email.endsWith(domain)
          )

          if (isAllowedDomain) {
            setRole('admin')
          } else {
            await signOut(auth)
            setUser(null)
            setToken(null)
            localStorage.clear()
            setLoading(false)
            setLoadingRole(false)
            return
          }
        }
        const userToken = await currentUser.getIdToken(true)
        if (isTokenExpired(userToken)) {
          if (!isAutoLogoutCalledRef.current) {
            isAutoLogoutCalledRef.current = true
            notifyError('Session expired, please log in again')
            logout()
          }
        } else {
          localStorage.setItem('token', userToken)
          setToken(userToken)
          setUser(currentUser)
        }
      } else {
        setUser(null)
        localStorage.clear()
      }
      setLoading(false)
      setLoadingRole(false)
    })

    auth.setPersistence(browserLocalPersistence).catch((error) => {
      setLoading(false)
      setLoadingRole(false)
    })

    return () => unsubscribe()
  }, [logout])

  useEffect(() => {
    if (selectedClientId) {
      localStorage.setItem('selectedClientId', selectedClientId)
    } else {
      localStorage.removeItem('selectedClientId')
    }
  }, [selectedClientId])

  const login = useCallback(async () => {
    if (isLoggingIn.current || loading) return
    isLoggingIn.current = true

    try {
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)

      const userData = result?.user
      const email = userData.email ?? ''
      const isAllowedDomain = allowedDomains.some((domain) =>
        email.endsWith(domain)
      )
      if (isAllowedDomain) {
        setRole('admin')
        localStorage.setItem('role', 'admin')
      } else {
        notifyError('Unauthorized email, login failed')
        await signOut(auth)
        return
      }
      const accessToken = await userData.getIdToken(true)
      notifySuccess('Login Successful')
      localStorage.setItem('user', JSON.stringify(userData))
      localStorage.setItem('token', accessToken)
      setToken(accessToken)
      setUser(userData)
      setLoadingRole(false)
    } catch (error: any) {
      notifyError('Login failed')
    } finally {
      isLoggingIn.current = false
    }
  }, [loading, token])

  const value = useMemo(
    () => ({
      user,
      token,
      role,
      login,
      logout,
      refreshToken,
      loading,
      loadingRole,
      selectedClientId,
      setSelectedClientId
    }),
    [
      token,
      user,
      role,
      login,
      logout,
      loading,
      loadingRole,
      refreshToken,
      selectedClientId,
      setSelectedClientId
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}